import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ElementRef,
  EnvironmentInjector,
  createNgModuleRef,
  Input,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EntityPanelService } from '../../services/entity-panel.service';
import { ENTITY_REGISTRY, EntityEntry } from './entity-registry';

const MODULE_DISPLAY_NAMES: Record<string, string> = {
  'bill-of-lading': 'Bill of Lading',
  'port-call': 'Port Call',
  'shared': 'Shared Entity',
  'pda': 'PDA',
  'da': 'DA',
  'finance': 'Finance',
  'legal': 'Legal',
};

@Component({
  selector: 'app-entity-panel',
  templateUrl: './entity-panel.component.html',
  standalone: false,
})
export class EntityPanelComponent implements OnInit, OnDestroy {
  @Input() currentModule: string = '';
  @ViewChild('entityHost', { read: ViewContainerRef }) entityHost!: ViewContainerRef;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  isOpen = false;
  selectedEntity: EntityEntry | null = null;
  loadingEntity = false;
  searchText = '';

  private subs = new Subscription();

  constructor(
    private entityPanelService: EntityPanelService,
    private injector: EnvironmentInjector,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.entityPanelService.isOpen$.subscribe((open) => {
        this.isOpen = open;
        if (!open) {
          this.clearEntity();
        }
      }),
    );

    // DAs, PDAs e Templates editam navegando pra uma rota própria em vez de abrir modal, e o
    // painel não é dono de nenhum router-outlet (é só um ViewContainerRef solto, ver
    // selectEntity) — então essa navegação acontece atrás dele, deixando o painel antigo
    // sobreposto na tela. Fechar ao detectar navegação real resolve pra essas entidades e
    // qualquer futura sem precisar tocar em cada componente individualmente.
    this.subs.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe(() => {
          if (this.isOpen) {
            this.close();
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get availableEntities(): (EntityEntry & { displayModule: string })[] {
    const all = ENTITY_REGISTRY.map((e) => ({
      ...e,
      displayModule: MODULE_DISPLAY_NAMES[e.sourceModule] ?? e.sourceModule,
    }));
    if (!this.searchText.trim()) return all;
    const lower = this.searchText.toLowerCase();
    return all.filter(
      (e) => e.label.toLowerCase().includes(lower) || e.displayModule.toLowerCase().includes(lower),
    );
  }

  async selectEntity(entry: EntityEntry): Promise<void> {
    this.selectedEntity = entry;
    this.loadingEntity = true;

    try {
      const [ComponentClass, ModuleClass] = await Promise.all([
        entry.loadComponent(),
        entry.loadModule(),
      ]);

      this.entityHost.clear();
      const moduleRef = createNgModuleRef(ModuleClass, this.injector);
      const componentRef = this.entityHost.createComponent(ComponentClass, {
        ngModuleRef: moduleRef,
      });
      if ('embedded' in componentRef.instance) {
        componentRef.setInput('embedded', true);
      }
    } finally {
      this.loadingEntity = false;
    }
  }

  back(): void {
    this.clearEntity();
  }

  close(): void {
    this.entityPanelService.close();
  }

  private clearEntity(): void {
    this.selectedEntity = null;
    this.searchText = '';
    if (this.entityHost) this.entityHost.clear();
  }
}
